import { AxiosRequestConfig } from 'axios'
import config from 'lib/config'

const SERVER_TIME_OFFSET_KEY = 'server-time-offset'

const formatDateElementPadding = (element: number) => element.toString().padStart(2, '0')

const fetchServerTime = (): string | null => {
    try {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', '/api/server-time', false) // false = synchronous
        xhr.send()

        if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText)
            return response.timestamp
        }
    } catch (error) {
        console.warn('Failed to fetch server time:', error)
    }
    return null
}

const updateServerTimeOffset = (offset: number) => {
    localStorage.setItem(SERVER_TIME_OFFSET_KEY, offset.toString())
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMediaHeader = (apiConfig: AxiosRequestConfig<any>) => {
    // web platform
    const platform = 1

    const version = 1

    // path
    const path = apiConfig?.url ?? ''
    const pathWithLeadingSlash = path.startsWith('/') ? path : `/${path}`
    const pathLength = (142 + pathWithLeadingSlash.length).toString().padStart(5, '0')

    // IMPORTANT: We must use server time instead of client time for API validation
    // The server validates timestamps and expects them to be within a specific time window.
    // If users have incorrect system time on their devices (common issue), API calls would fail.
    // By using server-provided timestamp, we ensure all API requests pass validation regardless
    // of the user's device time settings.
    let adjustedTimeMs = Date.now()

    // Try to get server time using cached offset strategy
    if (typeof window !== 'undefined') {
        try {
            const now = Date.now()

            // Check if we have a cached time offset
            const cachedOffset = localStorage.getItem(SERVER_TIME_OFFSET_KEY)
            if (cachedOffset) {
                const offset = parseInt(cachedOffset)

                // Fetch fresh server time to check if offset needs updating
                const serverTime = fetchServerTime()
                if (serverTime) {
                    const currentOffset = parseInt(serverTime) - now
                    const offsetDifference = Math.abs(currentOffset - offset)
                    const ONE_HOUR_MS = 60 * 60 * 1000 // 1 hour in milliseconds

                    // Only update offset if difference is more than 1 hour
                    if (offsetDifference > ONE_HOUR_MS) {
                        updateServerTimeOffset(currentOffset)
                        adjustedTimeMs = now + currentOffset
                    } else {
                        // Use existing offset
                        adjustedTimeMs = now + offset
                    }
                } else {
                    // Server time fetch failed, use existing offset
                    adjustedTimeMs = now + offset
                }
            } else {
                // No cached offset, fetch server time and create initial offset
                const serverTime = fetchServerTime()
                if (serverTime) {
                    const offset = parseInt(serverTime) - now
                    updateServerTimeOffset(offset)
                    adjustedTimeMs = now + offset
                }
            }
        } catch (error) {
            // If anything fails, fallback to client time
            console.warn('Failed to get server time, using client time:', error)
        }
    }

    const adjustedTime = new Date(adjustedTimeMs)

    const timeFormat = [
        adjustedTime.getUTCFullYear(),
        formatDateElementPadding(adjustedTime.getUTCMonth() + 1),
        formatDateElementPadding(adjustedTime.getUTCDate()),
        formatDateElementPadding(adjustedTime.getUTCHours()),
        formatDateElementPadding(adjustedTime.getUTCMinutes()),
        formatDateElementPadding(adjustedTime.getUTCSeconds()),
    ].join('')

    const timeIdentifier = 3 * +timeFormat + 4321

    // random number
    const randomNumberMinimum = 1111
    const randomNumberMaximum = 9999
    const randomNumber = Math.floor(Math.random() * (randomNumberMaximum - randomNumberMinimum + 1)) + randomNumberMinimum

    // secret
    const secret = config.getApiSecretCode()
    const reversedSecret = secret.split('').reverse().join('')

    const header = `${platform}${version}${pathLength}${timeIdentifier}${randomNumber}${reversedSecret}`

    return { header, timeFormat }
}
